import { Component } from '@angular/core';
import app from 'App';
import { PriorityViewModel } from 'app/models/PriorityViewModel';
import { ViewLocatorService } from '../../../services/view-locator.service';
import { ActionService } from '../../../services/action.service';
import { UserSkipService } from '../../../services/user-skip.service';
import { LaunchpadThemeService, themes } from '../../../services/launchpad-theme.service';
import { UserService } from '../../../services/user.service';
import { SessionStorageService } from '../../../services/session-storage.service';
import { ReloadService } from '../../../services/reload.service';
import Utils from '../../../shared/utils';


@Component({
  selector: 'app-components-specialist-specialistworkspace',
  templateUrl: './SpecialistWorkspace.component.html',
  styleUrls: ['./SpecialistWorkspace.component.scss', '../../../shared/action-button.scss']
})
export class SpecialistWorkspaceComponent implements IActivatable {
  public theme: themes;
  public themes = themes;
  public customerButtonCss: string;
  public icon: string;
  public hasWebsiteBookmarkingPermissions: boolean;
  public taskTypes: Boo.Objects.TaskType[];
  public canFilterLanguages: boolean;
  public languageOptions: LanguageSelection[] = [];
  public userLanguages: Boo.Objects.UserLanguage[];
  public cmsOptions: Boo.Objects.ContentManagementSystem[] = [];

  // Filter id-lists for the next-task lookup. null = "no filter, every option
  // counts"; explicit number[] = "only these ids." The child
  // <app-multi-select-with-all> components emit this shape directly via
  // (idsChange).
  public taskTypeIds: number[] | null = null;
  public languageIds: number[] | null = null;
  public cmsIds: number[] | null = null;

  constructor(
    private viewLocatorService: ViewLocatorService,
    private actionService: ActionService,
    private userSkipService: UserSkipService,
    private themeService: LaunchpadThemeService,
    private userService: UserService,
    private sessionStorageService: SessionStorageService,
    private reloadService: ReloadService) { }

  private static autoGetNextRouteParam = 'next';

  public isLoading: KnockoutObservable<boolean>;

  public canActivate(user: Boo.Objects.User, partner: Boo.Objects.Partner) {
    this.taskTypes = user.PartnerUsers
      .find((pu) => pu.PartnerId == partner.PartnerId && pu.UserLevelId == Boo.Objects.Enums.UserLevelEnum.Specialist)
      .TaskTypes
      .sort((a, b) => a.Name.localeCompare(b.Name));

    this.canFilterLanguages = user.Languages.length > 1;
    this.userLanguages = user.Languages;

    return true;
  }

  public activate(params: any): JQueryPromise<void> {
    this.isLoading = ko.observable(false);
    this.theme = this.themeService.getTheme();
    this.customerButtonCss = this.themeService.getNextButtonStyles[this.theme];
    this.icon = this.themeService.getSpecialistIcon();

    Utils.wrapDfd(this.sessionStorageService.getStaticData())
      .then((staticData: Boo.Objects.LaunchPadStaticData) => {
        this.cmsOptions = staticData.ContentManagementSystems
          .slice()
          .sort((a, b) => a.Name.localeCompare(b.Name));

        this.languageOptions = staticData.Languages
          .filter(x => this.userLanguages.map(y => y.LanguageId).includes(x.LanguageId))
          .map(x => ({
            languageId: x.LanguageId,
            fullName: x.Name
          }))
          .sort((a, b) => a.fullName.localeCompare(b.fullName));
      })
      .fail((err: any) => toastr.error(err));


    if (_.first(params) === SpecialistWorkspaceComponent.autoGetNextRouteParam) {
      this.viewLocatorService.removeFromDisplayedRoute(SpecialistWorkspaceComponent.autoGetNextRouteParam);
      return this.getNextTaskInQueue();
    }

    return Utils.wrapDfd(this.userService.canWorkTaskType(Boo.Objects.Enums.TaskTypeEnum.WebsiteBookmarking))
      .then(hasWebsiteBookmarkingPermissions => {
        this.hasWebsiteBookmarkingPermissions = hasWebsiteBookmarkingPermissions;
      });
  }

  public getNextTaskInQueue(): JQueryPromise<any> {
    this.isLoading(true);

    if (app.hasUserBeenNotifiedSessionExpired()) {
      app.notifySessionExpired();
      return;
    }

    // if reload is required, add /next to route and reload so getNextTaskInQueue gets called after reload
    if (this.reloadService.isReloadRequired()) {
      this.viewLocatorService.addToDisplayedRoute(SpecialistWorkspaceComponent.autoGetNextRouteParam, true);
      this.reloadService.reload('Auto reload from specialistworkspace getNextTaskInQueue');
      return;
    }

    return Utils.wrapDfd(this.actionService.getNextActionInQueue(
        this.taskTypeIds, this.languageIds, this.cmsIds))
      .then((data) => {
        if (!data || !data.Task) {
          toastr.warning('No more actions are available.');
        } else {
          PriorityViewModel.show('app-components-specialist-actionworkspace', { currentAction: data })
            .done((result: any) => {
              if (result) {
                // HACK: This is super hacky but we need to fix priority view to wait until the chain is done.
                setTimeout(this.getNextTaskInQueue.bind(this), 1000);
              }
            });
        }
      })
      .fail((err: any) => toastr.error(err))
      .always(() => {
        this.isLoading(false);
      });
  }

  public deleteWebsiteBookmarkingSkips(): void {
    Utils.wrapDfd(this.userSkipService.deleteCustomerSkips(Boo.Objects.Enums.TaskTypeEnum.WebsiteBookmarking))
      .then(() => {
          bootbox.alert('Your Website Bookmarking user skips have been deleted');
      })
      .fail((msg: string) => {
          toastr.error(msg);
      });
  }

  public toggleEasterEgg(): void {
    this.themeService.toggleEasterEgg();
    this.icon = this.themeService.getSpecialistIcon();
  }

  public onTaskTypeIdsChange(ids: number[] | null): void { this.taskTypeIds = ids; }
  public onLanguageIdsChange(ids: number[] | null): void { this.languageIds = ids; }
  public onCmsIdsChange(ids: number[] | null): void { this.cmsIds = ids; }
}

interface LanguageSelection {
  fullName: string;
  languageId: number;
}
